import React from 'react';
import CategorySelect, { CategorySelectType } from '../widgets/CategorySelect';
import {
  getCategoryCommonDefaultValue,
  getCategoryCommonBackValue,
  getMultipleSelectDefaultValue,
  getMultipleSelectNoneDefaultValue,
} from '../utils';
import { commonCategoryProps } from '../constants/category';
import RegionCountryItem from '@op/components/Snippets/RegionCountryItem';
import { allCountryMap, countryOptions } from '../constants/country';
import { TComponentConfig, TOptionsItem, TLanguageItem } from '../types';
import RadioAllMultipleSelect, {
  RadioAllMultipleSelectType,
} from '../components/RadioAllMultipleSelect';
import MultipleSelect, {
  IMultipleSelectType,
} from '../components/MultipleSelect';
import CheckboxFilter from '../components/CheckboxFilter';

export const VideoCategory: TComponentConfig<CategorySelectType> = {
  Component: CategorySelect,
  defaultValue: getCategoryCommonDefaultValue,
  backValue: getCategoryCommonBackValue,
  requestKey: 'marketing_hub_video_config_time_range_category',
  props: {
    ...commonCategoryProps,
    options: [],
    filterOptions: (
      options: any,
      filterValue: any,
      channelFilterValue?: string,
    ) => {
      const { channels } = filterValue;
      const list = options?.[channels || channelFilterValue] || [];

      return list.map((item: any) => {
        return {
          value: item.category_en.toLocaleLowerCase(),
          text: item.category_en,
          textCn: item.category_zh,
        };
      });
    },
  },
};

export const VideoCountryRegion: TComponentConfig<RadioAllMultipleSelectType> =
{
  Component: RadioAllMultipleSelect,
  defaultValue: (filterValue: any, props: RadioAllMultipleSelectType) => {
    const { valueKeys: key, options } = props;
    return getMultipleSelectDefaultValue(filterValue, key, options, []);
  },
  backValue: (filterValue: any, props: RadioAllMultipleSelectType) => {
    const { valueKeys: key, allValueConfig = 'all' } = props || {};
    const value = filterValue[key];
    return { [key]: value.length === 0 ? [allValueConfig] : value };
  },
  condDealwith: (condInfo: any, key: string) => {
    const data = condInfo[key];
    // 使用 Map 来去重，以 country_code 为键
    const countryMap = new Map<string, TOptionsItem>();

    // 遍历所有地区和国家的数据
    data?.forEach?.((region: any) => {
      if (region.region_code === 'ALL') return;

      region.countries.forEach((country: any) => {
        const { country_code, country_en, country_zh, languages } = country;
        // 如果 country_code 不存在于 Map 中，则添加
        if (!countryMap.has(country_code)) {
          countryMap.set(country_code, {
            value: country_code.toLocaleLowerCase(),
            text: country_en,
            textCn: country_zh,
            languages,
          });
        }
      });
    });

    // 将 Map 转换为数组并返回
    return Array.from(countryMap.values());
  },
  requestKey: 'marketing_hub_hashtag_config_country_region',
  props: {
    title: 'Country/Region',
    titleCn: '国家/地区',
    valueKeys: 'countries',
    options: countryOptions,
    leftText: 'Country',
    ...allCountryMap,
    hasSelectAllBtn: true,
    matchButtonWidth: true,
    itemRender: (option, isEn) => {
      const languages = option.languages || [];
      const countryName = isEn ? option.text : option.textCn;
      const langText =
        languages.length > 0
          ? languages
            .map((lang: TLanguageItem) =>
              isEn ? lang.language_en : lang.language_cn,
            )
            .join(', ')
          : '';
      const bubbleText = langText ? `${countryName} - ${langText}` : undefined;
      return (
        <RegionCountryItem
          style={{ display: 'inline-flex', height: 24 }}
          iconStyle={{ width: 16 }}
          market={option.value}
          bubbleText={bubbleText}
        />
      );
    },
  },
};

function defaultValueFunc(filterValue: any, props: IMultipleSelectType) {
  const { options, valueKeys: key } = props;
  const list = options || [];
  return getMultipleSelectNoneDefaultValue(
    filterValue,
    key,
    list,
    list.map((item) => item.value),
  );
}

function backValueFunc(filterValue: any, props: IMultipleSelectType) {
  const { valueKeys: key, options } = props;
  const list = (options || []).map((item) => item.value);
  const values = filterValue?.[key] || [];
  if (values.length === list.length) {
    return { [key]: [] };
  }
  if (values.length === 0) {
    return { [key]: ["-99999"] };
  }
  return { [key]: values };
}

export const VideoMediaTypes: TComponentConfig<IMultipleSelectType> = {
  Component: MultipleSelect,
  defaultValue: defaultValueFunc,
  backValue: backValueFunc,
  props: {
    title: 'Media Type',
    titleCn: '媒体类型',
    valueKeys: 'media_types',
    options: [
      { text: 'Video', textCn: '视频', value: 'video' },
      { text: 'Post', textCn: '图文贴', value: 'post' },
    ],
  },
};

export const VideoXCategory: TComponentConfig<IMultipleSelectType> = {
  Component: MultipleSelect,
  defaultValue: defaultValueFunc,
  backValue: backValueFunc,
  requestKey: 'marketing_hub_x_video_config_categories',
  condDealwith: (condInfo: any, key: string) => {
    const data = condInfo?.[key] || [];
    return data?.map((item: any) => ({
      value: item.value,
      text: item.label_en,
      textCn: item.label_zh,
    }));
  },
  props: {
    ...commonCategoryProps,
    options: [],
    listHeight: 300
  },
};

export const VideoDouyinCategory: TComponentConfig<IMultipleSelectType> = {
  Component: MultipleSelect,
  defaultValue: defaultValueFunc,
  backValue: backValueFunc,
  requestKey: 'marketing_hub_douyin_video_config_categories',
  condDealwith: (condInfo: any, key: string) => {
    const data = condInfo?.[key] || [];
    return data?.map((item: any) => ({
      value: item.value,
      text: item.label_en,
      textCn: item.label_zh,
    }));
  },
  props: {
    ...commonCategoryProps,
    options: [],
    listHeight: 300
  },
};

export const VideoRelatedLanguages: TComponentConfig<any> = {
  Component: CheckboxFilter,
  defaultValue: () => ({ related_languages_only: false }),
  backValue: (filterValue: any) => {
    const depValue = filterValue?.countries || [];
    if (
      !Array.isArray(depValue) ||
      depValue.length === 0 ||
      depValue.some((v: string) => ['all', 'global'].includes(v))
    ) {
      return { related_languages_only: false };
    }
    return { related_languages_only: filterValue.related_languages_only ?? false };
  },
  props: {
    valueKeys: 'related_languages_only',
    label: 'Related Languages only',
    labelCn: '仅主语种相关',
    dependencyKey: 'countries',
    allValues: ['all', 'global'],
    tipsMap: {
      en: 'Available when certain countries are selected; once checked, the list displays only videos in the primary language(s) of those countries.',
      zh: '选择特定国家时可用，勾选之后列表仅展示国家主语种相关的视频',
    },
  },
};
